<template>
  <div class="tool-page">
    <nav class="breadcrumb">
      <router-link to="/">{{ $t('nav.home') }}</router-link>
      <span class="separator">›</span>
      <span class="current">{{ $t('uuidGen.title') }}</span>
    </nav>

    <div class="tool-header">
      <h1>{{ $t('uuidGen.title') }}</h1>
      <p>{{ $t('uuidGen.subtitle') }}</p>
    </div>

    <div class="tool-card">
      <div class="uuid-display">
        <code class="uuid-text">{{ currentUuid }}</code>
        <button class="btn-icon" @click="copyUuid(currentUuid)" title="copy">📋</button>
        <button class="btn-icon" @click="generateOne" title="regenerate">🔄</button>
      </div>

      <div class="settings">
        <div class="setting-row">
          <label>{{ $t('uuidGen.version') }}</label>
          <div class="toggle-group">
            <button :class="['toggle-btn', { active: version === 4 }]" @click="version = 4; generateOne()">UUID v4</button>
            <button :class="['toggle-btn', { active: version === 1 }]" @click="version = 1; generateOne()">UUID v1</button>
          </div>
        </div>
        <div class="setting-row">
          <label>{{ $t('uuidGen.caseLabel') }}</label>
          <div class="toggle-group">
            <button :class="['toggle-btn', { active: !uppercase }]" @click="uppercase = false; generateOne()">{{ $t('uuidGen.lower') }}</button>
            <button :class="['toggle-btn', { active: uppercase }]" @click="uppercase = true; generateOne()">{{ $t('uuidGen.upper') }}</button>
          </div>
        </div>
        <div class="setting-row">
          <label class="checkbox-label">
            <input type="checkbox" v-model="withBraces" @change="generateOne" />
            {{ $t('uuidGen.withBraces') }}
          </label>
          <label class="checkbox-label">
            <input type="checkbox" v-model="noDashes" @change="generateOne" />
            {{ $t('uuidGen.noDashes') }}
          </label>
        </div>
      </div>

      <div class="batch-section">
        <h3>{{ $t('uuidGen.batchTitle') }}</h3>
        <div class="batch-controls">
          <label>{{ $t('uuidGen.batchCount') }}</label>
          <input type="number" v-model.number="batchCount" min="1" max="500" class="batch-input" />
          <button class="btn btn-primary" @click="generateBatch">{{ $t('uuidGen.batchGenerate') }}</button>
          <button class="btn btn-outline" @click="copyBatch" v-if="batchList.length">{{ $t('common.copy') }}</button>
        </div>
        <textarea v-if="batchList.length" :value="batchList.join('\n')" rows="8" readonly class="batch-output"></textarea>
      </div>

      <div class="action-bar" v-if="statusMsg">
        <span class="status-msg">{{ statusMsg }}</span>
      </div>
    </div>

    <div class="tool-card info-card">
      <h2>{{ $t('uuidGen.whatIs') }}</h2>
      <p>{{ $t('uuidGen.whatIsDesc') }}</p>
      <h3>{{ $t('uuidGen.useCasesTitle') }}</h3>
      <ul>
        <li v-for="(item, i) in $t('uuidGen.useCases')" :key="i">{{ item }}</li>
      </ul>
      <h3>{{ $t('uuidGen.tipsTitle') }}</h3>
      <ul>
        <li v-for="(item, i) in $t('uuidGen.tips')" :key="i">{{ item }}</li>
      </ul>
    </div>
  </div>
</template>

<script>
export default {
  name: 'UuidGenerator',
  data() {
    return {
      currentUuid: '',
      version: 4,
      uppercase: false,
      withBraces: false,
      noDashes: false,
      batchCount: 20,
      batchList: [],
      statusMsg: ''
    }
  },
  mounted() { this.generateOne() },
  methods: {
    generateUuidV4() {
      // 使用 crypto.getRandomValues 生成安全的 UUID v4
      const bytes = new Uint8Array(16)
      crypto.getRandomValues(bytes)
      bytes[6] = (bytes[6] & 0x0f) | 0x40
      bytes[8] = (bytes[8] & 0x3f) | 0x80
      const hex = Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('')
      return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`
    },
    generateUuidV1() {
      // 简化的 UUID v1 实现（基于时间戳）
      const now = Date.now()
      const ticks = (now + 12219292800000) * 10000
      const timeLow = (ticks & 0xFFFFFFFF).toString(16).padStart(8, '0')
      const timeMid = ((ticks >>> 32) & 0xFFFF).toString(16).padStart(4, '0')
      const timeHiVer = (((ticks >>> 48) & 0x0FFF) | 0x1000).toString(16).padStart(4, '0')
      const bytes = new Uint8Array(8)
      crypto.getRandomValues(bytes)
      bytes[0] = (bytes[0] & 0x3f) | 0x80
      const clockSeq = Array.from(bytes.slice(0, 2)).map(b => b.toString(16).padStart(2, '0')).join('')
      const node = Array.from(bytes.slice(2)).map(b => b.toString(16).padStart(2, '0')).join('')
      return `${timeLow}-${timeMid}-${timeHiVer}-${clockSeq}-${node}`
    },
    formatUuid(uuid) {
      if (this.noDashes) uuid = uuid.replace(/-/g, '')
      if (this.uppercase) uuid = uuid.toUpperCase()
      if (this.withBraces) uuid = `{${uuid}}`
      return uuid
    },
    generateOne() {
      const raw = this.version === 4 ? this.generateUuidV4() : this.generateUuidV1()
      this.currentUuid = this.formatUuid(raw)
    },
    generateBatch() {
      const list = []
      for (let i = 0; i < this.batchCount; i++) {
        const raw = this.version === 4 ? this.generateUuidV4() : this.generateUuidV1()
        list.push(this.formatUuid(raw))
      }
      this.batchList = list
    },
    async copyUuid(uuid) {
      try {
        await navigator.clipboard.writeText(uuid)
        this.statusMsg = this.$t('common.copied')
        setTimeout(() => { this.statusMsg = '' }, 2000)
      } catch { this.statusMsg = this.$t('common.copyFailed') }
    },
    async copyBatch() {
      try {
        await navigator.clipboard.writeText(this.batchList.join('\n'))
        this.statusMsg = this.$t('common.copied')
        setTimeout(() => { this.statusMsg = '' }, 2000)
      } catch { this.statusMsg = this.$t('common.copyFailed') }
    }
  }
}
</script>

<style scoped>
.tool-page { max-width: 800px; margin: 0 auto; }
.breadcrumb { margin-bottom: 16px; font-size: 14px; color: var(--text-secondary); }
.breadcrumb a { color: var(--accent-color); text-decoration: none; }
.breadcrumb .separator { margin: 0 8px; }
.tool-header { margin-bottom: 24px; }
.tool-header h1 { font-size: 28px; margin-bottom: 8px; color: var(--text-primary); }
.tool-header p { color: var(--text-secondary); font-size: 15px; }
.tool-card { background: var(--bg-card); border: 1px solid var(--border-color); border-radius: 16px; padding: 24px; margin-bottom: 24px; box-shadow: var(--shadow-sm); }
.info-card h2 { font-size: 20px; margin-bottom: 12px; color: var(--text-primary); }
.info-card h3 { font-size: 16px; margin: 16px 0 8px; color: var(--text-primary); }
.info-card p { font-size: 14px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 12px; }
.info-card ul { padding-left: 20px; margin-bottom: 12px; }
.info-card li { font-size: 14px; color: var(--text-secondary); line-height: 1.8; margin-bottom: 4px; }

.uuid-display { display: flex; align-items: center; gap: 12px; padding: 16px 20px; background: var(--bg-tertiary); border-radius: 12px; margin-bottom: 20px; }
.uuid-text { flex: 1; font-family: 'SF Mono', Monaco, Consolas, monospace; font-size: 18px; color: var(--text-primary); word-break: break-all; }
.btn-icon { background: none; border: none; cursor: pointer; font-size: 20px; padding: 4px 8px; border-radius: 6px; transition: background 0.2s; }
.btn-icon:hover { background: var(--border-color); }

.settings { display: flex; flex-direction: column; gap: 12px; margin-bottom: 24px; }
.setting-row { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.setting-row > label:first-child { font-size: 14px; font-weight: 600; color: var(--text-primary); min-width: 60px; }
.toggle-group { display: flex; gap: 8px; }
.toggle-btn { padding: 6px 16px; border: 1px solid var(--border-color); border-radius: 6px; background: var(--bg-input); color: var(--text-secondary); cursor: pointer; font-size: 13px; transition: all 0.2s; }
.toggle-btn.active { background: var(--accent-color); color: white; border-color: var(--accent-color); }
.checkbox-label { display: flex; align-items: center; gap: 8px; font-size: 14px; color: var(--text-secondary); cursor: pointer; }
.checkbox-label input { accent-color: var(--accent-color); }

.batch-section { border-top: 1px solid var(--border-color); padding-top: 20px; }
.batch-section h3 { font-size: 16px; margin-bottom: 12px; color: var(--text-primary); }
.batch-controls { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; flex-wrap: wrap; }
.batch-controls label { font-size: 14px; color: var(--text-secondary); }
.batch-input { width: 60px; padding: 6px 8px; border: 1px solid var(--border-color); border-radius: 6px; text-align: center; background: var(--bg-input); color: var(--text-primary); }
.batch-output { width: 100%; padding: 12px; border: 1px solid var(--border-color); border-radius: 8px; background: var(--bg-input); color: var(--text-primary); font-family: 'SF Mono', monospace; font-size: 13px; resize: vertical; box-sizing: border-box; }
.btn { padding: 8px 18px; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; border: none; transition: all 0.2s; }
.btn-primary { background: var(--accent-color); color: white; }
.btn-primary:hover { opacity: 0.9; }
.btn-outline { background: transparent; color: var(--text-primary); border: 1px solid var(--border-color); }
.btn-outline:hover { border-color: var(--accent-color); color: var(--accent-color); }
.action-bar { margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--border-color); }
.status-msg { font-size: 13px; color: var(--accent-color); }
</style>
